import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { CalendarClock, Loader2, CheckCircle2, RotateCcw, MapPin, Phone, MessageSquarePlus, Send, Undo2, CalendarPlus, X, Trash2, FileSpreadsheet, Pencil } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PianificareFilters, filterGroups } from "../components/PianificareFilters";
import { exportPianificatiToExcel } from "../lib/exportPianificati";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

function ClienteCard({ c, planned, squadre, onMarked, onPlanned, onRemoved, onUpdated }) {
  const [notes, setNotes] = useState(c.notes || []);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [acting, setActing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  const remove = async () => {
    if (!window.confirm("Rimuovere questo cliente dalla lista 'Installazioni da Pianificare'?")) return;
    setRemoving(true);
    try {
      await api.post(`/installazioni-da-pianificare/${c.id}/rimuovi`);
      toast.success("Rimosso dalla lista");
      onRemoved?.(c.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRemoving(false);
    }
  };

  const addNote = async () => {
    const t = text.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/installazioni-da-pianificare/${c.id}/note`, { text: t });
      setNotes((prev) => [...prev, data]);
      setText("");
      toast.success("Nota aggiunta");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const toggle = async () => {
    setActing(true);
    try {
      const ep = planned ? "unmark-planned" : "mark-planned";
      await api.post(`/installazioni-da-pianificare/${c.id}/${ep}`);
      toast.success(planned ? "Riportato in lista" : "Segnato come pianificato");
      onMarked?.(c.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="card-shell p-4" data-testid={`dp-cliente-${c.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/clienti/${c.id}`} className="font-bold hover:underline truncate" style={{ fontFamily: "Manrope" }}>
              {[c.nome, c.cognome_ragione_sociale].filter(Boolean).join(" ") || "—"}
            </Link>
            {c.cliente_id && <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{c.cliente_id}</span>}
          </div>
          <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            {c.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.telefono}</span>}
            {(c.citta || c.indirizzo) && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[c.indirizzo, c.citta].filter(Boolean).join(", ")}</span>
            )}
          </div>
          {planned && (
            <div className="mt-1.5">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: "rgba(16,185,129,0.12)", color: "#047857", border: "1px solid rgba(16,185,129,0.35)" }}
                data-testid={`dp-planned-badge-${c.id}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Pianificato{c.planned_at ? ` il ${fmtDate(c.planned_at)}` : ""}
                {c.planned_by_name ? ` · ${c.planned_by_name}` : ""}
              </span>
            </div>
          )}

          {/* Storico note */}
          <div className="mt-3 space-y-1.5" data-testid={`dp-notes-${c.id}`}>
            {notes.length === 0 && <div className="text-[11px] italic" style={{ color: "var(--text-muted)" }}>Nessuna nota</div>}
            {notes.map((n) => (
              <div key={n.id} className="text-xs rounded-md px-2.5 py-1.5" style={{ background: "var(--surface-muted)" }} data-testid={`dp-note-${n.id}`}>
                <div>{n.text}</div>
                <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {n.author_name} · {fmtDateTime(n.created_at)}
                </div>
              </div>
            ))}
          </div>

          {/* Aggiungi nota */}
          <div className="mt-2 flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
              placeholder="Aggiungi una nota…"
              className="flex-1 px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid={`dp-note-input-${c.id}`}
            />
            <button onClick={addNote} disabled={adding || !text.trim()} className="btn-secondary text-xs disabled:opacity-50" data-testid={`dp-note-add-${c.id}`}>
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />} Aggiungi
            </button>
          </div>
        </div>

        <div className="shrink-0 flex flex-col gap-2">
          {!planned && (
            <button
              onClick={() => setPlanOpen(true)}
              className="btn-primary"
              data-testid={`dp-plan-now-${c.id}`}
            >
              <CalendarPlus className="w-4 h-4" /> Pianifica ora
            </button>
          )}
          <button
            onClick={toggle}
            disabled={acting}
            className="btn-secondary"
            data-testid={`dp-toggle-${c.id}`}
          >
            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : planned ? <><Undo2 className="w-4 h-4" /> Riporta in lista</> : <><CheckCircle2 className="w-4 h-4" /> Pianificato</>}
          </button>
          {planned && (
            <button
              onClick={() => setPlanOpen(true)}
              className="btn-secondary"
              data-testid={`dp-edit-plan-${c.id}`}
            >
              <Pencil className="w-4 h-4" /> Modifica pianificazione
            </button>
          )}
          {!planned && (
            <button
              onClick={remove}
              disabled={removing}
              className="btn-secondary text-xs"
              style={{ color: "#B30000", borderColor: "#FCA5A5" }}
              title="Rimuovi dalla lista (inserito per errore)"
              data-testid={`dp-remove-${c.id}`}
            >
              {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Rimuovi dalla lista
            </button>
          )}
        </div>
      </div>
      {planOpen && (
        <PlanNowModal
          cid={c.id}
          nome={[c.nome, c.cognome_ragione_sociale].filter(Boolean).join(" ")}
          squadre={squadre}
          onClose={() => setPlanOpen(false)}
          onSaved={() => { setPlanOpen(false); if (planned) onUpdated?.(); else onPlanned?.(c.id); }}
        />
      )}
    </div>
  );
}

function PlanNowModal({ cid, nome, squadre, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [squadra, setSquadra] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/clienti/${cid}/pianificazione`);
        if (data?.data_inizio) {
          setDate(String(data.data_inizio).slice(0, 10));
          const t = String(data.data_inizio).slice(11, 16);
          if (t) setTime(t);
        }
        if (data?.squadra_id) setSquadra(data.squadra_id);
      } catch {}
    })();
  }, [cid]);

  const save = async () => {
    if (!date) { toast.error("Seleziona una data"); return; }
    setSaving(true);
    try {
      const { data: existing } = await api.get(`/clienti/${cid}/pianificazione`);
      const payload = {
        pannelli: existing?.pannelli || "",
        batteria: existing?.batteria || "",
        inverter: existing?.inverter || "",
        squadra_id: squadra || existing?.squadra_id || "",
        data_inizio: `${date}T${time || "09:00"}:00`,
        durata_ore: existing?.durata_ore ?? 4.0,
        note: existing?.note || "",
        vitto: existing?.vitto ?? 0.0,
        alloggio: existing?.alloggio ?? 0.0,
      };
      await api.put(`/clienti/${cid}/pianificazione`, payload);
      toast.success("Pianificazione salvata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="dp-plan-modal">
      <div className="card-shell w-full max-w-md p-6" style={{ background: "#fff" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--primary)" }}>
              <CalendarPlus className="w-3.5 h-3.5" /> Pianifica installazione
            </div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{nome || "—"}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-black/5" data-testid="dp-plan-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Data</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)" }} data-testid="dp-plan-date" />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Ora inizio</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)" }} data-testid="dp-plan-time" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Squadra</label>
            <select value={squadra} onChange={(e) => setSquadra(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="dp-plan-squadra">
              <option value="">— Nessuna squadra —</option>
              {(squadre || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="dp-plan-cancel">Annulla</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="dp-plan-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InstallazioniDaPianificare() {
  const [params, setParams] = useSearchParams();
  const commessaFilter = params.get("commessa") || "";
  const [tab, setTab] = useState("da_pianificare"); // da_pianificare | pianificati
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [squadre, setSquadre] = useState([]);
  const [provinciaFilter, setProvinciaFilter] = useState("");
  const [regioneFilter, setRegioneFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [plannedFrom, setPlannedFrom] = useState("");
  const [plannedTo, setPlannedTo] = useState("");
  const groupRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const planned = tab === "pianificati";
      const { data } = await api.get(`/installazioni-da-pianificare?planned=${planned}`);
      setGroups(data.groups || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/squadre"); setSquadre(data || []); } catch {} })();
  }, []);

  useEffect(() => {
    if (commessaFilter && !loading && groupRefs.current[commessaFilter]) {
      groupRefs.current[commessaFilter].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [commessaFilter, loading, groups]);

  const handleMarked = (cid) => {
    setGroups((prev) => prev
      .map((g) => ({ ...g, clienti: g.clienti.filter((c) => c.id !== cid) }))
      .filter((g) => g.clienti.length > 0));
    setTotal((t) => Math.max(0, t - 1));
  };

  const visibleGroups = filterGroups(groups, { commessa: commessaFilter, provincia: provinciaFilter, regione: regioneFilter, search: searchFilter, plannedFrom, plannedTo });
  if (tab === "pianificati") {
    visibleGroups.forEach((g) => g.clienti.sort((a, b) => {
      const da = String(a.data_pianificazione || a.planned_at || "");
      const db = String(b.data_pianificazione || b.planned_at || "");
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    }));
  }

  return (
    <div data-testid="da-pianificare-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--primary)" }}>
          <CalendarClock className="w-3.5 h-3.5" /> Da pianificare
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Installazioni da Pianificare</h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
          Clienti (per commessa) senza installazione completata e senza data di pianificazione.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setTab("da_pianificare")}
          className={tab === "da_pianificare" ? "btn-primary" : "btn-secondary"}
          data-testid="dp-tab-da-pianificare"
        >
          Da pianificare
        </button>
        <button
          onClick={() => setTab("pianificati")}
          className={tab === "pianificati" ? "btn-primary" : "btn-secondary"}
          data-testid="dp-tab-pianificati"
        >
          Già pianificati
        </button>
        {tab === "pianificati" && (
          <button
            onClick={() => exportPianificatiToExcel(visibleGroups, "installazioni_pianificate", Object.fromEntries((squadre || []).map((s) => [s.id, s.name])))}
            className="btn-secondary ml-auto"
            data-testid="dp-export-excel"
          >
            <FileSpreadsheet className="w-4 h-4" /> Esporta Excel
          </button>
        )}
      </div>

      <PianificareFilters
        groups={groups}
        commessa={commessaFilter}
        onCommessa={(v) => setParams(v ? { commessa: v } : {})}
        provincia={provinciaFilter}
        onProvincia={setProvinciaFilter}
        regione={regioneFilter}
        onRegione={setRegioneFilter}
        search={searchFilter}
        onSearch={setSearchFilter}
        plannedFrom={plannedFrom}
        onPlannedFrom={setPlannedFrom}
        plannedTo={plannedTo}
        onPlannedTo={setPlannedTo}
        showPlannedDate={tab === "pianificati"}
        testPrefix="dp"
      />

      {loading ? (
        <div className="card-shell p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
      ) : visibleGroups.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#10B981" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="dp-empty">
            {tab === "pianificati" ? "Nessun cliente già pianificato." : "Nessun cliente da pianificare. Ottimo lavoro!"}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {visibleGroups.map((g) => (
            <div key={g.commessa_id || "_none"} ref={(el) => { groupRefs.current[g.commessa_id || ""] = el; }} data-testid={`dp-group-${g.commessa_id || "none"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: "var(--primary)", color: "#fff" }}>
                  {g.commessa_name}
                </div>
                <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {g.clienti.length} client{g.clienti.length === 1 ? "e" : "i"}
                </div>
              </div>
              <div className="space-y-3">
                {g.clienti.map((c) => (
                  <ClienteCard key={c.id} c={c} planned={tab === "pianificati"} squadre={squadre} onMarked={handleMarked} onPlanned={handleMarked} onRemoved={handleMarked} onUpdated={load} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
